import { ensureSession } from "./supabase";
import type {
  CharacterSheet,
  CharacterStat,
  StoryModel,
  StorySchema,
} from "@/story/model";

// ---------------------------------------------------------------------------
// Row shapes (snake_case, as stored by PostgREST)
// ---------------------------------------------------------------------------

export interface ProfileRow {
  id: string;
  display_name: string;
}

export interface CharacterRow {
  id: string;
  author_id: string;
  title: string;
  role: string;
  blurb: string;
  avatar_emoji: string;
  color: string;
  stats: CharacterStat[];
  traits: string[];
  tags: string[];
  public: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoryRow {
  id: string;
  author_id: string;
  title: string;
  subtitle: string;
  description: string;
  cover_emoji: string;
  cover_color: string;
  schema: StorySchema;
  model: StoryModel;
  ink_source: string;
  ink_json: Record<string, unknown> | string | null;
  published: boolean;
  plays: number;
  created_at: string;
  updated_at: string;
  author?: { display_name?: string } | null;
}

export interface StoryCastRow {
  story_id: string;
  character_id: string;
  characters?: CharacterRow | null;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

export function characterRowToSheet(row: CharacterRow): CharacterSheet {
  return {
    id: row.id,
    title: row.title ?? "",
    role: row.role ?? "",
    blurb: row.blurb ?? "",
    avatarEmoji: row.avatar_emoji ?? "🧑‍💻",
    color: row.color ?? "#3ddc97",
    stats: Array.isArray(row.stats) ? (row.stats as CharacterStat[]) : [],
    traits: Array.isArray(row.traits) ? row.traits : [],
    tags: Array.isArray(row.tags) ? row.tags : [],
    public: Boolean(row.public),
  };
}

// ---------------------------------------------------------------------------
// Auth / profile
// ---------------------------------------------------------------------------

export async function getOrCreateProfile(
  displayName = "Anonymous Dev"
): Promise<{ profile: ProfileRow | null; error: string | null }> {
  const authed = await ensureSession();
  if (!authed) return { profile: null, error: "Supabase is not configured" };
  const { client, user } = authed;

  const { error } = await client
    .from("profiles")
    .upsert(
      { id: user.id, display_name: displayName },
      { onConflict: "id", ignoreDuplicates: true }
    );
  if (error) return { profile: null, error: error.message };

  const fresh = await client
    .from("profiles")
    .select("id, display_name")
    .eq("id", user.id)
    .maybeSingle();
  return { profile: fresh.data ?? null, error: fresh.error?.message ?? null };
}

export async function updateDisplayName(name: string): Promise<string | null> {
  const authed = await ensureSession();
  if (!authed) return null;
  const { client, user } = authed;
  const { error } = await client
    .from("profiles")
    .update({ display_name: name })
    .eq("id", user.id);
  return error?.message ?? null;
}

// ---------------------------------------------------------------------------
// Characters
// ---------------------------------------------------------------------------

export async function listCharacters(mine: boolean): Promise<CharacterRow[]> {
  const authed = await ensureSession();
  if (!authed) return [];
  const query = mine
    ? authed.client
        .from("characters")
        .select("*")
        .eq("author_id", authed.user.id)
    : authed.client.from("characters").select("*").eq("public", true);
  const { data } = await query.order("updated_at", { ascending: false });
  return (data ?? []) as CharacterRow[];
}

export async function getCharacter(id: string): Promise<CharacterRow | null> {
  const authed = await ensureSession();
  if (!authed) return null;
  const { data } = await authed.client
    .from("characters")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  return (data ?? null) as CharacterRow | null;
}

export async function saveCharacter(
  sheet: CharacterSheet
): Promise<{ row: CharacterRow | null; error: string | null }> {
  const authed = await ensureSession();
  if (!authed) return { row: null, error: "Supabase is not configured" };

  const payload = {
    id: sheet.id || crypto.randomUUID(),
    title: sheet.title,
    role: sheet.role,
    blurb: sheet.blurb,
    avatar_emoji: sheet.avatarEmoji,
    color: sheet.color,
    stats: sheet.stats,
    traits: sheet.traits,
    tags: sheet.tags,
    public: sheet.public,
  };

  const { data, error } = await authed.client
    .from("characters")
    .upsert(payload);
  return {
    row: (data?.[0] ?? null) as CharacterRow | null,
    error: error?.message ?? null,
  };
}

export async function deleteCharacter(id: string): Promise<string | null> {
  const authed = await ensureSession();
  if (!authed) return "Supabase is not configured";
  const { error } = await authed.client
    .from("characters")
    .delete()
    .eq("id", id);
  return error?.message ?? null;
}

// ---------------------------------------------------------------------------
// Stories
// ---------------------------------------------------------------------------

export async function listPublishedStories(): Promise<StoryRow[]> {
  const authed = await ensureSession();
  if (!authed) return [];
  const { data } = await authed.client
    .from("stories")
    .select("*, author:profiles(display_name)")
    .eq("published", true)
    .order("created_at", { ascending: false })
    .limit(60);
  return (data ?? []) as StoryRow[];
}

export async function listMyStories(): Promise<StoryRow[]> {
  const authed = await ensureSession();
  if (!authed) return [];
  const { data } = await authed.client
    .from("stories")
    .select("*")
    .eq("author_id", authed.user.id)
    .order("updated_at", { ascending: false });
  return (data ?? []) as StoryRow[];
}

export async function getStory(id: string): Promise<StoryRow | null> {
  const authed = await ensureSession();
  if (!authed) return null;
  const { data } = await authed.client
    .from("stories")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  return (data ?? null) as StoryRow | null;
}

export interface StoryDraft {
  id?: string;
  title: string;
  subtitle: string;
  description: string;
  coverEmoji: string;
  coverColor: string;
  schema: StorySchema;
  model: StoryModel;
  inkSource: string;
  inkJson: Record<string, unknown> | null;
  published: boolean;
}

export async function saveStory(
  draft: StoryDraft
): Promise<{ row: StoryRow | null; error: string | null }> {
  const authed = await ensureSession();
  if (!authed) return { row: null, error: "Supabase is not configured" };

  const payload = {
    id: draft.id || crypto.randomUUID(),
    title: draft.title,
    subtitle: draft.subtitle,
    description: draft.description,
    cover_emoji: draft.coverEmoji,
    cover_color: draft.coverColor,
    schema: draft.schema,
    model: draft.model,
    ink_source: draft.inkSource,
    ink_json: draft.inkJson,
    published: draft.published,
  };

  const { data, error } = await authed.client.from("stories").upsert(payload);
  return {
    row: (data?.[0] ?? null) as StoryRow | null,
    error: error?.message ?? null,
  };
}

export async function setStoryPublished(
  id: string,
  published: boolean
): Promise<string | null> {
  const authed = await ensureSession();
  if (!authed) return "Supabase is not configured";
  const { error } = await authed.client
    .from("stories")
    .update({ published })
    .eq("id", id);
  return error?.message ?? null;
}

export async function deleteStory(id: string): Promise<string | null> {
  const authed = await ensureSession();
  if (!authed) return "Supabase is not configured";
  const { error } = await authed.client.from("stories").delete().eq("id", id);
  return error?.message ?? null;
}

export async function incrementPlays(id: string): Promise<void> {
  const authed = await ensureSession();
  if (!authed) return;
  await authed.client.rpc("bump_story_plays", { story_id: id });
}

// ---------------------------------------------------------------------------
// Cast (story_characters join)
// ---------------------------------------------------------------------------

export async function getStoryCast(storyId: string): Promise<CharacterRow[]> {
  const authed = await ensureSession();
  if (!authed) return [];
  const { data } = await authed.client
    .from("story_characters")
    .select("characters(*)")
    .eq("story_id", storyId);
  const rows = (data ?? []) as unknown as StoryCastRow[];
  const out: CharacterRow[] = [];
  for (const row of rows) {
    if (row.characters) out.push(row.characters);
  }
  return out;
}

export async function setStoryCast(
  storyId: string,
  characterIds: string[]
): Promise<string | null> {
  const authed = await ensureSession();
  if (!authed) return "Supabase is not configured";
  const sb = authed.client;

  const { error: delError } = await sb
    .from("story_characters")
    .delete()
    .eq("story_id", storyId);
  if (delError) return delError.message;

  if (characterIds.length === 0) return null;

  const rows = characterIds.map((character_id) => ({
    story_id: storyId,
    character_id,
  }));
  const { error } = await sb.from("story_characters").insert(rows);
  return error?.message ?? null;
}
